import React, { useCallback, useEffect, useState } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import {
  ChevronRight, Phone, Mail, MapPin, Building2, Briefcase, Truck, ShieldCheck,
  Bell, PackageOpen, AlertCircle, Wrench, Hammer, Users, Leaf, Wallet, Percent,
  FileText, Star, Image, Home, Loader2, Plus, Trash2, Type as TypeIcon, Grid as GridIcon, ListOrdered, HelpCircle,
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../api/client';
import { useSettings } from '../context/SettingsContext';
import { useAuth } from '../context/AuthContext';
import EditableText from '../components/admin/EditableText';
import SEO from '../components/SEO';
import FAQSection from '../components/FAQSection';

const ICONS = {
  Phone, Mail, MapPin, Building2, Briefcase, Truck, ShieldCheck, Bell,
  PackageOpen, AlertCircle, Wrench, Hammer, Users, Leaf, Wallet, Percent,
  FileText, Star, Image, Home,
};
const ICON_LIST = Object.keys(ICONS);

const NEW_SECTIONS = {
  text: { kind: 'text', heading: 'New heading', body: 'New body — click to edit.' },
  grid: { kind: 'grid', heading: 'New grid heading', items: [
    { icon: 'ShieldCheck', title: 'Card title', body: 'Card body — click to edit.', meta: '' },
  ] },
  steps: { kind: 'steps', heading: 'How it works', items: [
    { title: 'Step 1', body: 'Step body — click to edit.' },
  ] },
  faq: { kind: 'faq', heading: 'FAQ', items: [
    { q: 'A question?', a: 'The answer.' },
  ] },
};

const StaticPage = () => {
  const { slug } = useParams();
  const { settings } = useSettings();
  const { user } = useAuth();
  const isAdmin = !!user?.is_admin;
  const [page, setPage] = useState(null);
  const [status, setStatus] = useState('loading');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let alive = true;
    setStatus('loading'); setPage(null);
    api.get(`/pages/${slug}`)
      .then(r => { if (alive) { setPage(r.data); setStatus('ok'); } })
      .catch(() => { if (alive) setStatus('404'); });
    return () => { alive = false; };
  }, [slug]);

  const persist = useCallback(async (next) => {
    if (!isAdmin) return;
    setPage(next);
    setSaving(true);
    try {
      const { slug: _s, ...rest } = next;
      await api.put(`/admin/pages/${next.slug}`, rest);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  }, [isAdmin]);

  const patch = (updater) => persist(updater(page));

  if (status === 'loading') {
    return <div className="min-h-[50vh] flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-[#F46A25]" /></div>;
  }
  if (status === '404' || !page) return <Navigate to="/" replace />;

  const setSection = (idx, section) => patch(p => ({ ...p, sections: p.sections.map((s, i) => i === idx ? section : s) }));
  const removeSection = (idx) => {
    if (!window.confirm('Remove this section?')) return;
    patch(p => ({ ...p, sections: p.sections.filter((_, i) => i !== idx) }));
  };
  const addSection = (kind) => patch(p => ({ ...p, sections: [...(p.sections || []), JSON.parse(JSON.stringify(NEW_SECTIONS[kind]))] }));

  return (
    <div className="bg-neutral-50">
      <SEO
        title={page.title}
        description={page.subtitle || `${page.title} — Dr Tools help & information.`}
        path={`/pages/${page.slug}`}
      />
      {isAdmin && (
        <div className="bg-[#F46A25] text-white text-xs font-bold uppercase tracking-wide px-4 py-2 flex items-center justify-between" data-testid="static-page-admin-bar">
          <span>Admin edit mode — click any text to edit. Changes save automatically.</span>
          {saving && <span className="flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" /> saving…</span>}
        </div>
      )}

      {/* Hero */}
      <div className="bg-[#12263A] text-white">
        <div className="max-w-[1200px] mx-auto px-4 py-14 md:py-20">
          <nav className="flex items-center gap-2 text-xs text-white/60 mb-4">
            <Link to="/" className="hover:text-[#F46A25]">Home</Link>
            <ChevronRight className="w-3 h-3" />
            <InlineText value={page.breadcrumb} isAdmin={isAdmin} onSave={(v) => persist({ ...page, breadcrumb: v })} className="text-white/70" placeholder="Breadcrumb group" />
            <ChevronRight className="w-3 h-3" />
            <span className="text-white font-semibold">{page.title}</span>
          </nav>
          <InlineText value={page.title} isAdmin={isAdmin} onSave={(v) => persist({ ...page, title: v })}
            className="text-4xl md:text-6xl font-black tracking-tight mb-3 block" placeholder="Page title" />
          <InlineText value={page.subtitle || ''} isAdmin={isAdmin} onSave={(v) => persist({ ...page, subtitle: v })}
            className="text-base md:text-lg text-white/70 max-w-3xl block" placeholder="Subtitle" multiline />
          <div className="mt-6 inline-flex items-center gap-2 text-[10px] uppercase tracking-widest text-[#F46A25] font-bold">
            <span className="w-6 h-px bg-[#F46A25]" /> {settings.brand_name || 'Dr Tools'} · {page.breadcrumb}
          </div>
        </div>
      </div>

      {/* Body */}
      <div className="max-w-[1200px] mx-auto px-4 py-12 md:py-16 space-y-14">
        {(page.sections || []).map((section, idx) => (
          <Section
            key={idx}
            section={section}
            idx={idx}
            isAdmin={isAdmin}
            onChange={(s) => setSection(idx, s)}
            onRemove={() => removeSection(idx)}
          />
        ))}

        {isAdmin && (
          <div className="border-2 border-dashed border-neutral-300 rounded-sm p-4 flex items-center gap-2 flex-wrap" data-testid="add-section-bar">
            <span className="text-xs uppercase font-bold text-neutral-500 mr-2">Add section:</span>
            <AddSectionBtn onClick={() => addSection('text')} icon={TypeIcon} label="Text block" />
            <AddSectionBtn onClick={() => addSection('grid')} icon={GridIcon} label="Grid cards" />
            <AddSectionBtn onClick={() => addSection('steps')} icon={ListOrdered} label="Numbered steps" />
            <AddSectionBtn onClick={() => addSection('faq')} icon={HelpCircle} label="FAQ list" />
          </div>
        )}

        {page.cta && (
          <section className="bg-[#12263A] text-white rounded-sm p-8 md:p-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-6" data-testid="static-cta">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-[#F46A25] font-bold mb-1">{settings.brand_name || 'Dr Tools'}</div>
              <h3 className="text-2xl font-black">Ready to go?</h3>
            </div>
            <div className="flex flex-wrap items-center gap-3">
              {['primary', 'secondary'].map(role => page.cta?.[role] && (
                <div key={role} className="flex items-center gap-1">
                  <a href={page.cta[role].href} className={role === 'primary' ? 'bg-[#F46A25] hover:bg-[#e05a15] text-white font-bold text-sm px-5 py-3 rounded-sm' : 'bg-transparent border border-white/30 hover:border-[#F46A25] hover:text-[#F46A25] text-white font-bold text-sm px-5 py-3 rounded-sm'}>
                    {page.cta[role].label}
                  </a>
                  {isAdmin && (
                    <span className="text-[10px] text-white/60 flex flex-col ml-1">
                      <InlineText value={page.cta[role].label} isAdmin={isAdmin} className="text-white/80" onSave={(v) => persist({ ...page, cta: { ...page.cta, [role]: { ...page.cta[role], label: v } } })} />
                      <InlineText value={page.cta[role].href} isAdmin={isAdmin} className="font-mono text-white/50" onSave={(v) => persist({ ...page, cta: { ...page.cta, [role]: { ...page.cta[role], href: v } } })} />
                    </span>
                  )}
                </div>
              ))}
            </div>
          </section>
        )}
      </div>
      {page.slug === 'contact' && (
        <FAQSection slug="contact" heading="Contacting Dr Tools — common questions" />
      )}
    </div>
  );
};

// -- Inline text that swaps to EditableText for admins, plain text otherwise --
const InlineText = ({ value, isAdmin, onSave, className = '', placeholder = 'Click to edit', multiline = false }) => {
  if (!isAdmin) return <span className={className}>{value || ''}</span>;
  return (
    <EditableText
      value={value || ''}
      onSave={onSave}
      className={`${className} hover:bg-white/10 rounded px-0.5`}
      multiline={multiline}
    />
  );
};

const AddSectionBtn = ({ icon: Icon, label, onClick }) => (
  <button onClick={onClick} className="text-xs bg-white border border-neutral-300 hover:border-[#F46A25] font-bold px-3 py-1.5 rounded-sm flex items-center gap-1">
    <Icon className="w-3 h-3" /> {label} <Plus className="w-3 h-3" />
  </button>
);

// ------- Section renderers -------
const AdminChrome = ({ isAdmin, onRemove, children, kind }) => {
  if (!isAdmin) return <>{children}</>;
  return (
    <div className="relative group border border-transparent hover:border-orange-200 rounded-sm p-2 -m-2">
      <div className="absolute -top-3 right-2 flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
        <span className="text-[10px] uppercase font-bold bg-[#12263A] text-white px-2 py-0.5 rounded-sm">{kind}</span>
        <button onClick={onRemove} className="text-white bg-red-500 hover:bg-red-600 rounded-full w-6 h-6 flex items-center justify-center" aria-label="Remove section">
          <Trash2 className="w-3 h-3" />
        </button>
      </div>
      {children}
    </div>
  );
};

const Section = ({ section, idx, isAdmin, onChange, onRemove }) => {
  const setHeading = (v) => onChange({ ...section, heading: v });
  const setBody = (v) => onChange({ ...section, body: v });
  const setItem = (i, next) => onChange({ ...section, items: section.items.map((it, k) => k === i ? next : it) });
  const addItem = (item) => onChange({ ...section, items: [...(section.items || []), item] });
  const removeItem = (i) => onChange({ ...section, items: section.items.filter((_, k) => k !== i) });

  if (section.kind === 'text') {
    return (
      <AdminChrome isAdmin={isAdmin} onRemove={onRemove} kind="Text block">
        <section data-testid={`static-section-${idx}`}>
          <h2 className="text-2xl md:text-3xl font-black text-[#12263A] mb-4">
            <InlineText value={section.heading} isAdmin={isAdmin} onSave={setHeading} placeholder="Section heading" />
          </h2>
          <div className="text-neutral-700 text-base leading-relaxed max-w-4xl whitespace-pre-line">
            <InlineText value={section.body} isAdmin={isAdmin} onSave={setBody} multiline placeholder="Body copy" />
          </div>
        </section>
      </AdminChrome>
    );
  }

  if (section.kind === 'grid') {
    return (
      <AdminChrome isAdmin={isAdmin} onRemove={onRemove} kind="Grid cards">
        <section data-testid={`static-section-${idx}`}>
          <h2 className="text-2xl md:text-3xl font-black text-[#12263A] mb-6">
            <InlineText value={section.heading} isAdmin={isAdmin} onSave={setHeading} />
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {section.items.map((it, i) => {
              const Icon = ICONS[it.icon] || Home;
              return (
                <div key={i} className="bg-white border border-neutral-200 rounded-sm p-6 hover:border-[#F46A25] transition-colors relative">
                  {isAdmin && (
                    <button onClick={() => removeItem(i)} className="absolute top-2 right-2 text-red-500 hover:bg-red-50 p-1 rounded" title="Remove card"><Trash2 className="w-3 h-3" /></button>
                  )}
                  <div className="w-11 h-11 rounded-sm bg-orange-50 text-[#F46A25] flex items-center justify-center mb-3"><Icon className="w-5 h-5" /></div>
                  {isAdmin && (
                    <select value={it.icon || 'Home'} onChange={e => setItem(i, { ...it, icon: e.target.value })} className="text-[10px] mb-2 border border-neutral-300 rounded-sm px-1 py-0.5">
                      {ICON_LIST.map(k => <option key={k}>{k}</option>)}
                    </select>
                  )}
                  <h3 className="text-base font-bold text-[#12263A] mb-2">
                    <InlineText value={it.title} isAdmin={isAdmin} onSave={v => setItem(i, { ...it, title: v })} placeholder="Card title" />
                  </h3>
                  <div className="text-sm text-neutral-700 leading-relaxed">
                    <InlineText value={it.body} isAdmin={isAdmin} onSave={v => setItem(i, { ...it, body: v })} multiline placeholder="Card body" />
                  </div>
                  <div className="mt-3 text-[11px] uppercase tracking-widest font-bold text-[#F46A25]">
                    <InlineText value={it.meta || ''} isAdmin={isAdmin} onSave={v => setItem(i, { ...it, meta: v })} placeholder="Optional meta" />
                  </div>
                </div>
              );
            })}
            {isAdmin && (
              <button onClick={() => addItem({ icon: 'ShieldCheck', title: 'New card', body: 'Body copy.', meta: '' })} className="border-2 border-dashed border-neutral-300 rounded-sm p-6 text-sm text-neutral-500 hover:border-[#F46A25] hover:text-[#F46A25] flex items-center justify-center gap-1"><Plus className="w-4 h-4" /> Add card</button>
            )}
          </div>
        </section>
      </AdminChrome>
    );
  }

  if (section.kind === 'steps') {
    return (
      <AdminChrome isAdmin={isAdmin} onRemove={onRemove} kind="Numbered steps">
        <section data-testid={`static-section-${idx}`}>
          <h2 className="text-2xl md:text-3xl font-black text-[#12263A] mb-6">
            <InlineText value={section.heading} isAdmin={isAdmin} onSave={setHeading} />
          </h2>
          <ol className="space-y-4">
            {section.items.map((it, i) => (
              <li key={i} className="flex gap-4 bg-white border border-neutral-200 rounded-sm p-5 relative">
                {isAdmin && <button onClick={() => removeItem(i)} className="absolute top-2 right-2 text-red-500 hover:bg-red-50 p-1 rounded" title="Remove step"><Trash2 className="w-3 h-3" /></button>}
                <div className="w-10 h-10 shrink-0 bg-[#F46A25] text-white font-black text-lg rounded-sm flex items-center justify-center">{i + 1}</div>
                <div className="flex-1">
                  <h3 className="text-base font-bold text-[#12263A] mb-1">
                    <InlineText value={it.title} isAdmin={isAdmin} onSave={v => setItem(i, { ...it, title: v })} placeholder="Step title" />
                  </h3>
                  <div className="text-sm text-neutral-700 leading-relaxed">
                    <InlineText value={it.body} isAdmin={isAdmin} onSave={v => setItem(i, { ...it, body: v })} multiline placeholder="Step body" />
                  </div>
                </div>
              </li>
            ))}
          </ol>
          {isAdmin && (
            <button onClick={() => addItem({ title: 'New step', body: 'Step body.' })} className="mt-3 border-2 border-dashed border-neutral-300 rounded-sm p-3 w-full text-sm text-neutral-500 hover:border-[#F46A25] hover:text-[#F46A25] flex items-center justify-center gap-1"><Plus className="w-4 h-4" /> Add step</button>
          )}
        </section>
      </AdminChrome>
    );
  }

  if (section.kind === 'faq') {
    return (
      <AdminChrome isAdmin={isAdmin} onRemove={onRemove} kind="FAQ list">
        <section data-testid={`static-section-${idx}`}>
          <h2 className="text-2xl md:text-3xl font-black text-[#12263A] mb-6">
            <InlineText value={section.heading} isAdmin={isAdmin} onSave={setHeading} />
          </h2>
          <div className="space-y-3">
            {section.items.map((it, i) => (
              <div key={i} className="bg-white border border-neutral-200 rounded-sm p-4 relative">
                {isAdmin && <button onClick={() => removeItem(i)} className="absolute top-2 right-2 text-red-500 hover:bg-red-50 p-1 rounded" title="Remove FAQ"><Trash2 className="w-3 h-3" /></button>}
                <div className="font-bold text-[#12263A] mb-2 flex items-center gap-2">
                  <HelpCircle className="w-4 h-4 text-[#F46A25]" />
                  <InlineText value={it.q} isAdmin={isAdmin} onSave={v => setItem(i, { ...it, q: v })} placeholder="Question" />
                </div>
                <div className="text-sm text-neutral-700 leading-relaxed pl-6">
                  <InlineText value={it.a} isAdmin={isAdmin} onSave={v => setItem(i, { ...it, a: v })} multiline placeholder="Answer" />
                </div>
              </div>
            ))}
          </div>
          {isAdmin && (
            <button onClick={() => addItem({ q: 'New question?', a: 'Answer.' })} className="mt-3 border-2 border-dashed border-neutral-300 rounded-sm p-3 w-full text-sm text-neutral-500 hover:border-[#F46A25] hover:text-[#F46A25] flex items-center justify-center gap-1"><Plus className="w-4 h-4" /> Add FAQ</button>
          )}
        </section>
      </AdminChrome>
    );
  }
  return null;
};

export default StaticPage;
